import type { CloseAiChatPanelProperties } from "@/analytics/ai-chat-events-properties";
import { EventType } from "@/analytics/analytics-events";
import { AccountSettingsDialog } from "@/components/ui/account-settings-dialog";
import { ExclusiveHeaderBar } from "@/components/ui/exclusive-header-bar";
import { ProgressOverviewMenu } from "@/components/ui/progress-overview/progress-overview-menu";
import { ProjectBreadcrumbs } from "@/components/ui/project-breadcrumbs";
import { QuickHelpButton } from "@/components/ui/quick-help-button";
import { SavingIndicator } from "@/components/ui/saving-indicator";
import { ShareLinkButton } from "@/components/ui/share-link-button";
import { ViewerHelpCenterMenu } from "@/components/ui/viewer-help-center-menu";
import { useIsDataPreparationReadOnly } from "@/data-preparation-tool/hooks/use-is-data-preparation-read-only";
import { useSaveUserEdits } from "@/data-preparation-tool/inspect-and-publish/refine-and-publish-utils";
import { waitForHierarchySaveToFinish } from "@/data-preparation-tool/store/data-preparation-ui/wait-for-hierarchy-save";
import { selectRegistrationRevision } from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { selectIsRevisionUserModified } from "@/data-preparation-tool/store/user-edits/data-preparation-user-edit-selectors";
import { SavingUserEditsDialog } from "@/data-preparation-tool/ui/data-preparation-dialogs/saving-user-edits-dialog";
import { DataPreparationSidebarBase } from "@/data-preparation-tool/ui/data-preparation-sidebar";
import { DraftSavingIndicator } from "@/data-preparation-tool/ui/draft-saving-indicator";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import {
  selectIsAiChatOpen,
  selectIsAiModelInitializing,
} from "@/features/ai-chat/ai-selectors";
import { setIsAiChatOpen } from "@/features/ai-chat/ai-slice";
import { Mode } from "@/modes/mode";
import { runtimeConfig } from "@/runtime-config";
import { ViewerTool } from "@/store/features/features";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectActiveAnalysis } from "@/store/point-cloud-analysis-tool-selector";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCanUseAiIndexing } from "@/store/subscription-features/subscription-features-selectors";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { selectCurrentUser } from "@/store/user-selectors";
import { APP_VERSION } from "@/utils/env-constants";
import {
  blue,
  FaroButton,
  FaroText,
  FeedbackIcon,
  HeaderBar,
  useBreakpointMdUp,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { selectDashboardUrl } from "@faro-lotv/project-source";
import { Stack } from "@mui/system";
import { useCallback, useMemo, useState, type JSX } from "react";
import { useMatch } from "react-router";
import { FeaturesDialogButton } from "../ui/features-dialog/features-dialog-button";
import { FaroAiChip } from "./ai-assets/ai-chip";

interface Props {
  /**
   * Pages dealing with modes need to provide the currently active mode.
   * If a mode has an exclusive header bar, ExclusiveHeaderBar will be used instead of HeaderBar.
   */
  mode?: Mode;
  /** The tool where the app header bar is shown. Used for feature selection. */
  tool?: ViewerTool;
  /** If provided, a feedback button will be shown in the header bar. */
  setIsFeedbackFormOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * @returns The Viewer's standard header bar based on flat-ui's HeaderBar component.
 */
export function AppHeaderBar({
  mode,
  tool = ViewerTool.sphereViewer,
  setIsFeedbackFormOpen,
}: Props): JSX.Element {
  const currentUser = useAppSelector(selectCurrentUser);
  // Only use the backend defined dashboard url so guest users of unlisted project
  // will not get redirected to the old HB dashboard by mistake
  const dashboardUrl = useAppSelector(selectDashboardUrl);
  const activeTool = useAppSelector(selectActiveTool);
  const activeAnalysis = useAppSelector(selectActiveAnalysis);
  const { requestLogin, logout } = useAuthContext();
  const isMdUp = useBreakpointMdUp();
  const [isSettingPageOpen, setIsSettingPageOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const isRevisionUserModified = useAppSelector(selectIsRevisionUserModified);
  const { handleErrorWithToast } = useErrorHandlers();
  const { isSavingUserEdits, saveUserEdits } = useSaveUserEdits();
  const revision = useAppSelector(selectRegistrationRevision);
  const [isWaitingForHierarchySave, setIsWaitingForHierarchySave] =
    useState(false);

  const isOnDraftPage = !!useMatch("/project/:projectId/draft/*");
  const isReadOnlyRevision = useIsDataPreparationReadOnly();

  const isAiChatOpen = useAppSelector(selectIsAiChatOpen);

  const isAiChatFeatureEnabled = useAppSelector(
    selectHasFeature(Features.AiChat),
  );
  const canUseAiIndexing = useAppSelector(selectCanUseAiIndexing);

  const isAiChatAllowed = isAiChatFeatureEnabled && canUseAiIndexing;

  const isAiModelInitializing = useAppSelector(selectIsAiModelInitializing);

  const toggleAiChat = useCallback(() => {
    if (isAiModelInitializing) return;

    if (isAiChatOpen) {
      Analytics.track<CloseAiChatPanelProperties>(EventType.closeAiChatPanel, {
        via: "top bar",
      });
    } else {
      Analytics.track(EventType.openAiChatPanel);
    }

    dispatch(setIsAiChatOpen(!isAiChatOpen));
  }, [dispatch, isAiChatOpen, isAiModelInitializing]);

  const links = useMemo(
    () => ({
      ...runtimeConfig.externalLinks,
      dashboardUrl,
    }),
    [dashboardUrl],
  );

  const saveBeforeNavigation = useCallback(async () => {
    // Only exiting the draft view for unsaved tree hierarchy changes should show the dialog.
    if (isOnDraftPage) {
      setIsWaitingForHierarchySave(true);
      try {
        await waitForHierarchySaveToFinish(store);
      } finally {
        setIsWaitingForHierarchySave(false);
      }
    }

    if (!isReadOnlyRevision && isRevisionUserModified && revision) {
      try {
        await saveUserEdits(revision);
      } catch (error) {
        handleErrorWithToast({
          error,
          title: "Failed to save edits before switching views",
        });
        throw error;
      }
    }
  }, [
    isReadOnlyRevision,
    isOnDraftPage,
    isRevisionUserModified,
    revision,
    saveUserEdits,
    handleErrorWithToast,
    store,
  ]);

  const canShareCurrentScene = !mode?.exclusive && mode?.name !== "start";

  // TODO: if we will ever unify sheet/walk/split/overview mode and we could re-use the
  // scene/overlay of this unified mode, then the Report "tool" can become a Report "mode"
  // and we can remove the lines below by defining CreateReportPanel as its ModePanel
  const isCreateReportToolActive =
    useAppSelector(selectActiveTool) === ToolName.createPDFReport;
  const drawerContent = useMemo(() => {
    if (isMdUp || isCreateReportToolActive) return;
    if (mode?.ModePanel) return <mode.ModePanel />;
    if (isOnDraftPage) {
      return <DataPreparationSidebarBase />;
    }
  }, [isCreateReportToolActive, isMdUp, mode, isOnDraftPage]);

  return mode?.exclusive && !mode.exclusive.useDefaultHeader ? (
    <ExclusiveHeaderBar
      {...mode.exclusive}
      hasQuickHelp={!!mode.ModeQuickHelpDrawer}
    />
  ) : (
    <HeaderBar
      userDisplayInfo={currentUser}
      links={links}
      onLogoClick={
        links.dashboardUrl
          ? async () => {
              await saveBeforeNavigation();
              window.location.assign(links.dashboardUrl ?? "/");
            }
          : undefined
      }
      appVersion={APP_VERSION}
      onLogInClick={requestLogin}
      onLogOutClick={async () => {
        await saveBeforeNavigation();
        logout();
      }}
      onAccountAndSecurityClick={() => setIsSettingPageOpen(true)}
      content={
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <ProjectBreadcrumbs />
          {isOnDraftPage ? <DraftSavingIndicator /> : <SavingIndicator />}
        </Stack>
      }
      drawerContent={drawerContent}
    >
      {(activeTool === ToolName.analysis || activeAnalysis?.isVisible) && (
        <FaroButton
          variant="ghost"
          icon={<FeedbackIcon />}
          onClick={() => {
            setIsFeedbackFormOpen?.(true);
          }}
        >
          <FaroText variant="bodyM" color={blue[600]}>
            Share your thoughts on the analysis tool
          </FaroText>
        </FaroButton>
      )}
      {isAiChatAllowed && (
        <FaroAiChip
          label="Ask AI"
          onClick={toggleAiChat}
          disabled={isAiModelInitializing}
          tooltip={
            isAiModelInitializing
              ? "Project is being analysed. See cloud activity menu at the top for progress."
              : undefined
          }
        />
      )}
      <FeaturesDialogButton tool={tool} />
      {canShareCurrentScene && <ShareLinkButton />}
      <ViewerHelpCenterMenu
        allowSwitchToPreviousUi={tool === ViewerTool.sphereViewer}
      />
      {mode?.ModeQuickHelpDrawer && <QuickHelpButton />}
      <ProgressOverviewMenu />

      {isSettingPageOpen && (
        <AccountSettingsDialog onClose={() => setIsSettingPageOpen(false)} />
      )}
      <SavingUserEditsDialog
        open={isSavingUserEdits || isWaitingForHierarchySave}
      />
    </HeaderBar>
  );
}
